/**
 * Settings panel: request an approval-gated change to a managed platform-config
 * key (report token, closeout callback wiring, Telegram wiring).
 *
 * The value never lands in config from the browser directly: this POSTs the
 * {key, value} to the server's in-memory pending store, which pushes a Telegram
 * approval prompt. The operator taps `/vault_approve <id>` and the server writes
 * the value to Vault (`secret/maestro/env`) with the scoped writer AppRole. So
 * the browser only *requests* the change — approval + the Vault write happen
 * server-side, gated by the same ceremony as the Gitea PAT rotation.
 *
 * Which keys and classes are offered comes from GET /api/config/managed-catalog,
 * which derives both from the predicates the POST routes enforce. This panel
 * holds no list of its own: the two it used to import were hand-copies that had
 * drifted from the server in three separate ways.
 */
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { KeyRound, RefreshCw } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  fetchManagedConfigCatalog,
  requestManagedConfigChange,
  rotateManagedSecret,
} from '@/lib/api';

/**
 * Which option a `<select>` should show, given the operator's explicit choice
 * and what the server currently offers.
 *
 * Three cases, and the third is the one that needs stating: an explicit choice
 * that is no longer in the catalog is discarded rather than kept. A `<select>`
 * whose `value` matches no `<option>` renders blank, so keeping it would show
 * an empty control that submits a key the server now rejects. Returns `''` when
 * nothing is on offer — the caller blocks submission on that.
 *
 * Pure and exported so the behaviour is tested without rendering; before the
 * catalog was fetched, the default was `MANAGED_CONFIG_KEYS[0]` and none of this
 * could arise.
 */
export function resolveSelectedOption(explicit: string, available: readonly string[]): string {
  if (explicit !== '' && available.includes(explicit)) return explicit;
  return available[0] ?? '';
}

export function ManagedConfigPanel(): React.ReactElement {
  // Both controls are driven by what the server says it accepts. The lists used
  // to be constants in `@/lib/api`, which drifted from the routes in three
  // places at once — see the comment on `fetchManagedConfigCatalog`.
  const catalogQuery = useQuery({
    queryKey: ['managed-config-catalog'],
    queryFn: fetchManagedConfigCatalog,
  });
  const settableKeys = catalogQuery.data?.settableKeys ?? [];
  const rotatable = catalogQuery.data?.rotatableClasses ?? [];

  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Derived at render rather than seeded by an effect — see resolveSelectedOption.
  const selectedKey = resolveSelectedOption(key, settableKeys);

  const requestMutation = useMutation({
    mutationFn: (input: { key: string; value: string }) =>
      requestManagedConfigChange(input.key, input.value),
    onMutate: () => {
      setError(null);
      setPendingId(null);
    },
    onSuccess: result => {
      setPendingId(result.id);
      setValue('');
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const trimmed = value.trim();
  const canRequest = trimmed.length > 0 && selectedKey !== '' && !requestMutation.isPending;

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!canRequest) return;
    requestMutation.mutate({ key: selectedKey, value: trimmed });
  };

  const [rotateClassId, setRotateClassId] = useState('');
  const [rotatePendingId, setRotatePendingId] = useState<string | null>(null);
  const [rotateError, setRotateError] = useState<string | null>(null);

  const selectedClassId = resolveSelectedOption(
    rotateClassId,
    rotatable.map(cls => cls.id)
  );

  const rotateMutation = useMutation({
    mutationFn: (classId: string) => rotateManagedSecret(classId),
    onMutate: () => {
      setRotateError(null);
      setRotatePendingId(null);
    },
    onSuccess: result => {
      setRotatePendingId(result.id);
    },
    onError: (err: Error) => {
      setRotateError(err.message);
    },
  });

  const handleRotate = (event: React.FormEvent): void => {
    event.preventDefault();
    if (rotateMutation.isPending || selectedClassId === '') return;
    rotateMutation.mutate(selectedClassId);
  };

  // A catalog that failed to load is reported, never silently rendered as an
  // empty dropdown — an empty list and an unreachable server look identical in
  // the markup otherwise.
  const catalogError = catalogQuery.isError
    ? catalogQuery.error instanceof Error
      ? catalogQuery.error.message
      : 'Failed to load the managed config catalog'
    : null;

  return (
    <div className="flex flex-col gap-6">
      <Card className="rounded-2xl border-border/70 bg-surface/95 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-primary" />
            Managed Config
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-sm text-text-secondary">
            Request a change to a managed platform secret. The value is sent to the server and held
            pending — approve the change from Telegram (<code>/vault_approve &lt;id&gt;</code>) to
            write it to Vault. The value is never shown or logged.
          </p>
          <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-text-secondary">Key</span>
              <select
                value={selectedKey}
                disabled={settableKeys.length === 0}
                onChange={event => {
                  setKey(event.target.value);
                }}
                className="h-10 rounded-md border border-border bg-surface px-3 font-mono text-sm text-text-primary"
              >
                {settableKeys.length === 0 && (
                  <option value="">{catalogQuery.isLoading ? 'Loading…' : 'Unavailable'}</option>
                )}
                {settableKeys.map(managedKey => (
                  <option key={managedKey} value={managedKey}>
                    {managedKey}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-1 flex-col gap-1 text-sm">
              <span className="text-text-secondary">New value</span>
              <Input
                type="password"
                autoComplete="off"
                value={value}
                onChange={event => {
                  setValue(event.target.value);
                }}
                placeholder="Enter the new value"
                className="font-mono"
              />
            </label>
            <Button type="submit" disabled={!canRequest}>
              {requestMutation.isPending ? 'Requesting…' : 'Request change'}
            </Button>
          </form>
          {pendingId && (
            <p className="mt-3 text-sm text-success">
              Requested. Approve in Telegram: <code>/vault_approve {pendingId}</code>
            </p>
          )}
          {error && <p className="mt-3 text-sm text-error">{error}</p>}
          {catalogError && <p className="mt-3 text-sm text-error">{catalogError}</p>}
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-border/70 bg-surface/95 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-primary" />
            Rotate a shared secret
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-sm text-text-secondary">
            Rotate a shared managed secret. The server generates the new value — approve the change
            from Telegram (<code>/vault_approve &lt;id&gt;</code>) to write it to Vault.
          </p>
          <form onSubmit={handleRotate} className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-text-secondary">Class</span>
              <select
                value={selectedClassId}
                disabled={rotatable.length === 0}
                onChange={event => {
                  setRotateClassId(event.target.value);
                }}
                className="h-10 rounded-md border border-border bg-surface px-3 font-mono text-sm text-text-primary"
              >
                {rotatable.length === 0 && (
                  <option value="">{catalogQuery.isLoading ? 'Loading…' : 'Unavailable'}</option>
                )}
                {rotatable.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {cls.field === undefined ? cls.id : `${cls.id} — ${cls.field}`}
                  </option>
                ))}
              </select>
            </label>
            <Button type="submit" disabled={rotateMutation.isPending || selectedClassId === ''}>
              {rotateMutation.isPending ? 'Rotating…' : 'Rotate'}
            </Button>
          </form>
          {rotatePendingId && (
            <p className="mt-3 text-sm text-success">
              Requested — approve in Telegram: <code>/vault_approve {rotatePendingId}</code>
            </p>
          )}
          {rotateError && <p className="mt-3 text-sm text-error">{rotateError}</p>}
          {catalogError && <p className="mt-3 text-sm text-error">{catalogError}</p>}
        </CardContent>
      </Card>
    </div>
  );
}
